/**
 * Render-prop provider that normalises a raw Invited App Shortcuts
 * recommendations response into `InvitedAppShortcutsRecommendationsResult`.
 *
 * ## Why this is a pure translator (not a Relay component)
 *
 * The previous version of this provider owned the `graphql\`...\`` document, the
 * `usePreloadedQuery` call, and the `__generated__/<Query>.graphql.ts` import.
 * That coupled the package to a single Relay artifact that lived inside this
 * package's `src/.../__generated__/` directory.
 *
 * Jira and Confluence entrypoint files cannot import that artifact:
 *
 * - Jira's `@atlassian/react-entrypoint/no-imports-in-entrypoint-file` lint rule
 *   restricts imports inside `*.entrypoint.tsx` files to a narrow allowlist
 *   (relative `*\/__generated__/*` paths, `@atlassian/ari/*`, the feature-gate
 *   modules, etc). A subpath export like
 *   `@atlassian/cross-join-nudges/.../recommendations-provider/query` matches
 *   `*\/__generated__/*` after module resolution, but the rule matches against
 *   the literal import-path string and so still rejects it.
 *
 * - There is no comparable rule in Confluence today, but Confluence's SSR
 *   preload pattern follows the same shape (load the query at route resolution
 *   time, render with `usePreloadedQuery`), and we want to keep both consumers
 *   on the same architecture.
 *
 * To unblock SSR preload in both products without a per-product allowlist
 * carve-out, this provider now operates on the **raw AGG response shape** that
 * consumers obtain from their own preloaded Relay query. Each consumer:
 *
 *   1. Owns a local `.graphql` file with the canonical query body (kept in sync
 *      via the drift test exported from `@atlassian/cross-join-nudges/test-helpers`).
 *   2. Preloads that query in their entrypoint and reads it with
 *      `usePreloadedQuery` in their renderer.
 *   3. Passes the resulting raw data to this provider as
 *      `recommendationsResponse`.
 *
 * This file therefore deliberately:
 *
 * - Does NOT `import` anything from `react-relay`. Consumers own the Relay
 *   integration; this file is pure data normalisation.
 * - Does NOT carry a `graphql\`...\`` template literal. The canonical query
 *   source is exported from `./test-helpers` so consumers (and the drift test)
 *   can read it as plain text.
 * - Defines a structural `InvitedAppShortcutsRecommendationsResponse` type that
 *   any Relay-generated type matching the canonical selection set will satisfy.
 *
 * For non-React callers (e.g. SSR adapters, unit tests, analytics middleware)
 * the same normalisation is available via the stand-alone
 * `buildInvitedAppShortcutsRecommendationsResult(response)` helper.
 */

import { type ReactNode } from 'react';

// =============================================================================
// Raw response contract
// =============================================================================
//
// This type mirrors the canonical query's selection set. Relay's generated
// query type (from each consumer's local `__generated__/*.graphql.ts`) will be
// structurally assignable to it as long as the consumer's query body matches
// the canonical text (enforced by the drift test in `./test-helpers`).
//
// Keep field optionality permissive: every leaf is `null | undefined`-tolerant
// because Relay generates nullable union shapes for `@nullable` GraphQL fields
// and for unmatched inline-spread variants.

export type InvitedAppShortcutsRecommendationsResponseGrowthRec = {
	readonly __typename: 'GrowthRecRecommendations';
	readonly context?: {
		readonly product?: string | null;
		readonly sessionId?: string | null;
		readonly tenantId?: string | null;
		readonly useCase?: string | null;
	} | null;
	readonly data?: ReadonlyArray<{
		readonly entityId: string;
		readonly reasons?: ReadonlyArray<string> | null;
		readonly score?: number | null;
	}> | null;
};

export type InvitedAppShortcutsRecommendationsResponseQueryError = {
	readonly __typename: 'QueryError';
	readonly extensions?: ReadonlyArray<{
		readonly errorType?: string | null;
		readonly statusCode?: number | null;
	}> | null;
	readonly identifier?: string | null;
	readonly message?: string | null;
};

export type InvitedAppShortcutsRecommendationsResponseUnknown = {
	// Tolerates Relay's `'%other'` __typename fallback for unrecognised union
	// members so we can fail closed without a runtime type error.
	readonly __typename: string;
};

export type InvitedAppShortcutsRecommendationsResponse = {
	readonly growthRecommendations?: {
		readonly recommendations?:
			| InvitedAppShortcutsRecommendationsResponseGrowthRec
			| InvitedAppShortcutsRecommendationsResponseQueryError
			| InvitedAppShortcutsRecommendationsResponseUnknown
			| null;
	} | null;
};

// =============================================================================
// Normalised result type (public, stable across consumers)
// =============================================================================

export type InvitedAppShortcutRecommendation = {
	entityId: string;
	reasons: ReadonlyArray<string>;
	score: number;
};

export type InvitedAppShortcutsRecommendationsContext = {
	product: string | null;
	sessionId: string | null;
	tenantId: string | null;
	useCase: string | null;
};

export type InvitedAppShortcutsRecommendationsResult =
	| {
			context: InvitedAppShortcutsRecommendationsContext;
			kind: 'recommendations';
			recommendations: ReadonlyArray<InvitedAppShortcutRecommendation>;
	  }
	| {
			errorType: string | null;
			identifier: string | null;
			kind: 'error';
			message: string | null;
			statusCode: number | null;
	  }
	| {
			kind: 'empty';
	  };

// =============================================================================
// Normalisation
// =============================================================================

/**
 * Translates a raw AGG response into the normalised
 * `InvitedAppShortcutsRecommendationsResult`. Pure function; safe to call
 * outside React (SSR adapters, tests, analytics middleware).
 */
export function buildInvitedAppShortcutsRecommendationsResult(
	recommendationsResponse: InvitedAppShortcutsRecommendationsResponse,
): InvitedAppShortcutsRecommendationsResult {
	const node = recommendationsResponse.growthRecommendations?.recommendations;
	if (!node) {
		return { kind: 'empty' };
	}
	if (node.__typename === 'GrowthRecRecommendations') {
		const growthRecNode = node as InvitedAppShortcutsRecommendationsResponseGrowthRec;
		const recommendations: ReadonlyArray<InvitedAppShortcutRecommendation> = (
			growthRecNode.data ?? []
		).map((item) => ({
			entityId: item.entityId,
			reasons: item.reasons ?? [],
			// GRS can return null/undefined; default to 0 so consumers can rank
			// without null-checking every score.
			score: item.score ?? 0,
		}));
		return {
			kind: 'recommendations',
			recommendations,
			context: {
				tenantId: growthRecNode.context?.tenantId ?? null,
				product: growthRecNode.context?.product ?? null,
				useCase: growthRecNode.context?.useCase ?? null,
				sessionId: growthRecNode.context?.sessionId ?? null,
			},
		};
	}
	if (node.__typename === 'QueryError') {
		const errorNode = node as InvitedAppShortcutsRecommendationsResponseQueryError;
		// `extensions` is an array of {statusCode, errorType} per the AGG schema;
		// surface the first entry which is what AGG conventionally populates.
		const firstExtension = errorNode.extensions?.[0];
		return {
			kind: 'error',
			identifier: errorNode.identifier ?? null,
			message: errorNode.message ?? null,
			statusCode: firstExtension?.statusCode ?? null,
			errorType: firstExtension?.errorType ?? null,
		};
	}
	return {
		kind: 'error',
		identifier: null,
		message: `Unknown InvitedAppShortcutsRecommendations response __typename: ${node.__typename}`,
		statusCode: null,
		errorType: null,
	};
}

// =============================================================================
// React provider
// =============================================================================

export type InvitedAppShortcutsRecommendationsProviderProps = {
	children: (result: InvitedAppShortcutsRecommendationsResult) => ReactNode;
	recommendationsResponse: InvitedAppShortcutsRecommendationsResponse;
};

/**
 * Render-prop wrapper around `buildInvitedAppShortcutsRecommendationsResult`.
 *
 * Implemented as a render-prop (not a hook) so the call site stays
 * declarative for the most common consumer shape: receive a Relay response
 * from `usePreloadedQuery`, render UI based on the normalised result.
 */
export function InvitedAppShortcutsRecommendationsProvider({
	recommendationsResponse,
	children,
}: InvitedAppShortcutsRecommendationsProviderProps): ReactNode {
	// No `useMemo`: `buildInvitedAppShortcutsRecommendationsResult` is a cheap
	// object walk + a `.map()` over `recommendations.data` (typically <10
	// items), and the wrapping `useMemo` only paid off when callers passed a
	// stable `recommendationsResponse` reference. Avoiding the memo removes
	// the foot-gun where fresh-object-literal callers silently re-run the
	// translator on every render and gives a single, predictable code path.
	return children(buildInvitedAppShortcutsRecommendationsResult(recommendationsResponse));
}
